import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiUpload, useAuth } from '@shop/api-client'
import type { Category, ProductListItem } from '../api/types'
import { Perm } from '../api/types'
import { ImageField } from '../ui/ImageField'
import { ProductThumb } from '../ui/ProductThumb'
import { Empty, ErrorBanner, Field, Panel, Select, Spinner, TextInput } from '@shop/ui'

/**
 * قائمةُ الأصناف.
 *
 * والبحثُ يُرسل إلى الخادم لا يُرشَّح في المتصفّح: تطبيعُ العربيّة (الألفُ
 * والهمزةُ والتاءُ المربوطة) يقع هناك بالجداول نفسِها التي تُطابق بها المسوّدات.
 * وترشيحٌ في الواجهة كان سيقول «لا نتائج» لاسمٍ موجودٍ بألفٍ مختلفة.
 */
export default function Products() {
  const [term, setTerm] = useState('')

  // ⚠ **والمفتوحُ واحدٌ لا مجموعة.** لوحُ الصورة فيه قاصٌّ يرسم على `canvas`،
  //   وفتحُ عشرةٍ منه في قائمةٍ واحدةٍ يثقل التبويبَ بلا أن يفيد: من يبدّل
  //   صورةً يبدّل واحدةً وينظر إليها.
  const [editing, setEditing] = useState<string | null>(null)

  const queries = useQueryClient()
  const { can } = useAuth()
  const canEdit = can(Perm.productEdit)

  // ⚠ **ورقمُ النسخة ليس زينة.** الصورةُ تُجلب بمسارٍ ثابتٍ هو معرّفُ الصنف،
  //   والمصغَّرةُ تُعيد الجلبَ حين يتغيّر المسار وحدَه. فلولا هذا الرقمُ لبقيت
  //   القديمةُ معروضةً بعد الاستبدال حتّى يُحدَّث التبويب — ويظنّ من بدّلها
  //   أنّ الرفعَ سقط، فيرفع ثانيةً.
  const [version, setVersion] = useState<Record<string, number>>({})

  const imagePath = (product: ProductListItem) => {
    if (!product.hasImage) {
      return null
    }

    const stamp = version[product.id]

    return `/api/v1/products/${product.id}/image${stamp === undefined ? '' : `?v=${stamp}`}`
  }

  const changed = async (id: string) => {
    setVersion((all) => ({ ...all, [id]: (all[id] ?? 0) + 1 }))

    await queries.invalidateQueries({ queryKey: ['products'] })
  }

  // ⚠ **و«بلا قسم» خيارٌ في المرشّح لا حالةٌ مخفيّة.**
  //
  //   القسمُ صار إلزاميًّا على الجديد، وما دخل قبل اليوم بلا قسمٍ يحتاج بابًا
  //   يُرى منه — وإلا بقي معلَّقًا إلى الأبد: لا يظهر في تقريرٍ بالقسم، ولا
  //   يُعرف أنّه ناقص.
  //
  // ⚠ **والمرشّحُ في العنوان لا في الحالة وحدَها**: شاشةُ الأقسام تقول «١٢
  //   صنفًا» وتربط إلى هنا، ورابطٌ يفتح قائمةً غيرَ مرشَّحةٍ يجعل العددَ
  //   كلامًا لا يُتحقَّق منه. والعنوانُ يُنسَخ ويُرسَل ويُحفظ كذلك.
  const [params, setParams] = useSearchParams()
  const section = params.get('categoryId') ?? ''

  const setSection = (value: string) => {
    setParams(value === '' ? {} : { categoryId: value }, { replace: true })
  }

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<Category[]>('/api/v1/catalog/categories'),
    staleTime: 5 * 60_000,
  })

  const scope =
    section === '' ? '' : section === 'none' ? '&uncategorized=true' : `&categoryId=${section}`

  const products = useQuery({
    queryKey: ['products', term, section],
    queryFn: () => api<ProductListItem[]>(
      `/api/v1/products?limit=200&q=${encodeURIComponent(term)}${scope}`),

    // النتائجُ السابقةُ تبقى أثناء الكتابة: وميضُ «يُحمَّل…» مع كلّ حرفٍ
    // يجعل القائمةَ ترتجف.
    placeholderData: keepPreviousData,
  })

  return (
    <Panel title="الأصناف">
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        {/*
          ⚠ **والباركودُ أوّلُ ما يُذكر**: من يقف أمام الرفّ يقرأ الرقمَ الملصوق
            ولا يعرف كيف كُتب الاسمُ في النظام. وحقلُ بحثٍ لا يقبل الماسحَ
            يدفعه إلى الاسم — فلا يجد الموجود، **فيُنشئه ثانيةً**.
        */}
        <Field label="بحث" hint="امسح باركودًا بالماسح — أو اكتب اسمًا أو ماركةً أو طرازًا أو رمزًا">
          <TextInput
            value={term}
            onChange={setTerm}
            placeholder="باركود · اسمٌ · ماركةٌ · طرازٌ · رمز…"
          />
        </Field>

        <Field label="القسم">
          <Select value={section} onChange={setSection}>
            <option value="">كلُّ الأقسام</option>
            <option value="none">بلا قسم</option>
            {(categories.data ?? []).filter((c) => c.isActive).map((category) => (
              <option key={category.id} value={category.id}>{category.nameAr}</option>
            ))}
          </Select>
        </Field>
      </div>

      {products.isPending && <Spinner />}
      {products.isError && <ErrorBanner error={products.error} />}
      {products.data?.length === 0 && <Empty>لا أصنافَ مطابقة.</Empty>}

      <ul className="space-y-2">
        {(products.data ?? []).map((product) => (
          <li key={product.id} className="rounded-md border border-line">
            {/*
              ⚠ **والمصغَّرةُ خارجَ `Link` لا داخلَه.** زرٌّ داخل رابطٍ يفتح
                الصفحةَ ويفتح اللوحَ معًا، ووقفُ الحدث بـ`preventDefault` يعمل
                حتّى يفتحها أحدُهم بزرّ الفأرة الأوسط أو بـ`Ctrl` — فالحدثُ لا
                يمرّ بالمعالج أصلًا. والفصلُ في البنية لا في المعالج.
            */}
            <div className="flex items-stretch gap-2 px-3 py-2">
              <ProductThumb
                path={imagePath(product)}
                alt={product.nameAr}
                onClick={
                  canEdit
                    ? () => setEditing((open) => (open === product.id ? null : product.id))
                    : undefined
                }
              />

              <Link to={`/products/${product.id}`} className="-my-2 block flex-1 py-2 hover:bg-surface">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium">{product.nameAr}</span>
                  <span className="tabular text-xs text-ink-soft" dir="ltr">
                    {product.code}
                  </span>
                </div>

                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                  {/* ⚠ والقسمُ يُعرض دائمًا — وغيابُه يُقال لا يُترك فراغًا. */}
                  {product.categoryName
                    ? <span className="rounded bg-surface px-1.5 font-medium">{product.categoryName}</span>
                    : <span className="rounded bg-warn/15 px-1.5 text-warn">بلا قسم</span>}
                  {product.brandName && <span>{product.brandName}</span>}
                  {product.model && <span dir="ltr">{product.model}</span>}
                  <span className="tabular">{product.variantCount} متغيّر</span>
                  {!product.isActive && <span className="text-warn">مؤرشف</span>}
                </div>
              </Link>
            </div>

            {editing === product.id && (
              <div className="border-t border-line px-3 py-3">
                <RowImage
                  product={product}
                  path={imagePath(product)}
                  onChanged={() => changed(product.id)}
                  onDone={() => setEditing(null)}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/**
 * لوحُ صورةِ الصنف داخلَ القائمة — <b>يُفتح تحت سطرِه ولا يُغادَر إليه</b>.
 *
 * ⚠ **ولماذا في القائمة أصلًا؟** الصورةُ لها مكانٌ في شاشة الصنف، لكنّ من يمرّ
 *   على مئةِ صنفٍ ليصلح صورَها كان يدخل ويخرج مئةَ مرّة — ويفقد موضعَه في
 *   القائمة وبحثَه ومرشّحَه في كلّ مرّة.
 *
 * والصلاحيّةُ مستعارةٌ لا جديدة: من يعدّل الصنفَ يبدّل صورتَه، وهي الصلاحيّةُ
 * نفسُها التي يفحصها الخادمُ على <c>/image</c>.
 */
function RowImage({
  product,
  path,
  onChanged,
  onDone,
}: {
  product: ProductListItem
  path: string | null
  onChanged: () => Promise<void>
  onDone: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)

    try {
      await work()
      await onChanged()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      <ImageField
        path={path}
        busy={busy}
        label={`صورةُ ${product.nameAr}`}
        hint="تُقصّ قبل أن تُرفع — وما يخرج من القاصّ هو ما يُحفظ."
        onPick={(file) => void run(() => apiUpload(`/api/v1/products/${product.id}/image`, file))}
        onRemove={
          product.hasImage
            ? () => void run(() => api(`/api/v1/products/${product.id}/image`, { method: 'DELETE' }))
            : undefined
        }
      />

      <ErrorBanner error={error} />

      <button
        type="button"
        onClick={onDone}
        disabled={busy}
        className="text-xs text-ink-soft underline disabled:opacity-50"
      >
        أغلق
      </button>
    </div>
  )
}
